import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Target, Users, CheckCircle } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section" style={{ background: 'var(--gradient-hero-subtle)', paddingTop: '4rem' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="heading-hero mb-4">About Emotional Intelligence Academy</h1>
            <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
              Building the emotional skills most of us were never taught, one week at a time.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1766973117841-e919298728e1" 
                  alt="Parent and child connecting"
                  className="rounded-lg shadow-lg w-full hover-lift"
                />
              </div>
              
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Heart size={32} style={{ color: 'var(--brand-orange)' }} />
                  <h2 className="heading-1">Our Mission</h2>
                </div>
                <p className="body-large mb-4" style={{ color: 'var(--text-secondary)' }}>
                  Most of us grew up without structured emotional education. We learned by trial and error, 
                  or we didn't learn at all. We want something better for our children.
                </p>
                <p className="body-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
                  This curriculum exists to give parents a clear, structured roadmap for teaching emotional 
                  intelligence—no guessing, no googling at 2 AM, no wondering if you're doing it right.
                </p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  One book per age. Scripts you can actually use. Practice that builds skills over time. 
                  A system that grows with your child from ages 4 through 18.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why It's Different Section */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Target size={48} className="mx-auto mb-4" style={{ color: 'var(--brand-dark-blue)' }} />
              <h2 className="heading-1 mb-4">How This Is Different</h2>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                This is not a workbook full of random prompts. It's a complete developmental system.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="card">
                <h3 className="heading-3 mb-3">Structure</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Every book has a 40-week sequence with clear weekly targets. You know exactly what 
                  skill you're building and when.
                </p>
              </div>

              <div className="card">
                <h3 className="heading-3 mb-3">Scripts</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  You get exact language to use in real moments—conflict, repair, boundaries, 
                  big feelings. No more searching for the right words.
                </p>
              </div>

              <div className="card">
                <h3 className="heading-3 mb-3">Repetition</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Skills are practiced repeatedly so they actually stick. This isn't a one-and-done 
                  approach—it's systematic development.
                </p>
              </div>

              <div className="card">
                <h3 className="heading-3 mb-3">Age-Specific Progression</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Each book is built for that exact developmental stage. What works for a 5-year-old 
                  doesn't work for a 15-year-old, and this curriculum honors that.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Users size={48} className="mx-auto mb-4" style={{ color: 'var(--brand-green)' }} />
              <h2 className="heading-1 mb-4">Our Values</h2>
            </div>

            <div className="space-y-6">
              <div className="card hover-lift">
                <div className="flex items-start gap-4">
                  <CheckCircle size={24} style={{ color: 'var(--brand-green)', flexShrink: 0 }} />
                  <div>
                    <h3 className="heading-3 mb-2">Calm & Respectful</h3>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      We believe emotional skills are taught best in calm moments with respectful connection, 
                      not through shame or force.
                    </p>
                  </div>
                </div>
              </div>

              <div className="card hover-lift">
                <div className="flex items-start gap-4">
                  <CheckCircle size={24} style={{ color: 'var(--brand-green)', flexShrink: 0 }} />
                  <div>
                    <h3 className="heading-3 mb-2">Clear Guidance</h3>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      Parents shouldn't have to guess. We provide scripts, examples, and step-by-step practice 
                      so you always know what to do next.
                    </p>
                  </div>
                </div>
              </div>

              <div className="card hover-lift">
                <div className="flex items-start gap-4">
                  <CheckCircle size={24} style={{ color: 'var(--brand-green)', flexShrink: 0 }} />
                  <div>
                    <h3 className="heading-3 mb-2">Realistic for Busy Families</h3>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      We know you're busy. Lessons are designed for 5-10 mins daily or 1 longer session per week, with flexible 
                      pacing options that work with real life.
                    </p>
                  </div>
                </div>
              </div>

              <div className="card hover-lift">
                <div className="flex items-start gap-4">
                  <CheckCircle size={24} style={{ color: 'var(--brand-green)', flexShrink: 0 }} />
                  <div>
                    <h3 className="heading-3 mb-2">Long-Term Development</h3>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      Emotional intelligence isn't built in a weekend workshop. This system gives you 14 years 
                      of structured progression from ages 4-18.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-1 mb-6">Who This Is For</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="card">
                <h3 className="heading-3 mb-2">Parents & Caregivers</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Who want a structured plan and scripts for teaching emotional skills at home
                </p>
              </div>

              <div className="card">
                <h3 className="heading-3 mb-2">Homeschool Families</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Looking for a complete social-emotional curriculum with ready-to-use lessons
                </p>
              </div>

              <div className="card">
                <h3 className="heading-3 mb-2">Educators & Therapists</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Who want an age-specific sequence they can recommend to families
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-1 mb-4">Start Building Emotional Intelligence Today</h2>
            <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
              Choose your child's age and begin with a curriculum that's built to last.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/start-here" className="btn-primary">
                Find Your Child's Book
              </Link>
              <Link to="/resources" className="btn-secondary">
                Pre-Order Books Ages 5-18
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
